import {
  ChangeSet,
  EditorSelection,
  EditorState,
  type Extension,
  RangeSet,
  RangeSetBuilder,
  RangeValue,
  StateField,
} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import type {Delimiter} from 'mdast'
import {visit} from 'unist-util-visit'

import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {blockquoteContinuationExtension} from './blockquote-continuation'
import {getMarkdownTree, markdownParserExtension, markdownTreeChanged} from './markdown-parser'

// --- Atomic ranges --- //

/** Set of delimiter token types that should be hidden from the user because they are always represented by markup. */
export const suppressedDelimiterTokenTypes = new Set<Delimiter['tokenType']>(['blockQuotePrefix'])

/**
 * `AtomicRangeValue` is just a simple `RangeValue` instance since `RangeValue` itself is abstract. Atomic ranges have
 * no metadata associated with them so there's no need to construct a whole `Decoration`.
 */
// Beware: I tried making this a singleton and it does not work. It looks like it works at first, but then everything breaks.
class AtomicRangeValue extends RangeValue {
  override eq() {
    return true
  }
}

interface SuppressedRangesState {
  decorations: DecorationSet
  atomicRanges: RangeSet<AtomicRangeValue>
}

/**
 * Build suppressed ranges for Markdown delimiters from the editor state. These are ranges of text that are hidden
 * from the user because they are always represented by markup or styling. Selection skips over them, and attempting
 * to delete them deletes the entire range at once - they act as a single character.
 *
 * This method automatically combines adjacent suppressed ranges into a single range.
 *
 * @see https://codemirror.net/docs/ref/#view.EditorView^atomicRanges
 */
function buildSuppressedRanges(state: EditorState): SuppressedRangesState {
  const tree = getMarkdownTree(state)
  const doc = state.doc.toString()

  const positions: Array<[start: number, to: number]> = []

  visit(tree, 'delimiter', delimiter => {
    if (isPositionedNode(delimiter) && suppressedDelimiterTokenTypes.has(delimiter.tokenType)) {
      const start = delimiter.position.start.offset
      const end = delimiter.position.end.offset
      const lastPosition = positions.at(-1)

      if (lastPosition?.[1] === start) {
        // Combine adjacent ranges
        lastPosition[1] = end
      } else {
        // Otherwise create a new range, expanding backwards to include preceding newline
        positions.push([start, end])
      }
    }
  })

  const decorationsBuilder = new RangeSetBuilder<Decoration>()
  const atomicRangesBuilder = new RangeSetBuilder<AtomicRangeValue>()
  for (const [start, end] of positions) {
    decorationsBuilder.add(start, end, Decoration.replace({}))

    // Atomic ranges also include the preceding newline so that, when backspaced, they delete everything up to the
    // previous line. This also provides one rendered whitespace character for selection indication
    const atomicRangeStart = doc[start - 1] === '\n' ? start - 1 : start
    atomicRangesBuilder.add(atomicRangeStart, end, new AtomicRangeValue())
  }

  return {
    decorations: decorationsBuilder.finish(),
    atomicRanges: atomicRangesBuilder.finish(),
  }
}

function mapSuppressedRanges(state: SuppressedRangesState, changes: ChangeSet): SuppressedRangesState {
  return {decorations: state.decorations.map(changes), atomicRanges: state.atomicRanges.map(changes)}
}

/** State field to provide suppressed ranges to the editor. */
const suppressedRangesState = StateField.define<SuppressedRangesState>({
  create: buildSuppressedRanges,
  update: (prev, tr) =>
    markdownTreeChanged(tr)
      ? buildSuppressedRanges(tr.state)
      : tr.docChanged
        ? mapSuppressedRanges(prev, tr.changes)
        : prev,
  provide: field => [
    markdownParserExtension,
    EditorView.decorations.from(field, s => s.decorations),
    EditorView.atomicRanges.from(field, s => () => s.atomicRanges),
  ],
})

/** When the user clicks on a suppressed range, move the selection to the end of the range. */
const suppressedRangeClickHandler = EditorView.domEventHandlers({
  mousedown(event, view) {
    const clickedPos = view.posAtCoords({x: event.clientX, y: event.clientY})
    if (clickedPos === null) return

    let targetPos = null

    // Using decorations rather than atomicRanges allows the user to still click on the line ending without jumping to
    // the next line's beginning
    view.state.field(suppressedRangesState).decorations.between(clickedPos, clickedPos, (_, to) => {
      targetPos = to
      return false // stop iterating - only one can ever overlap
    })

    if (targetPos !== null) {
      view.focus()
      view.dispatch({
        selection: EditorSelection.single(targetPos),
        userEvent: 'select.pointer',
      })
      return true
    }
  },
})

/**
 * Modifies the editor to apply Markdown-editing behaviors such as automatic continuation of blockquotes.
 */
export const markdownBehaviorsExtension: Extension = [
  suppressedRangesState,
  suppressedRangeClickHandler,
  blockquoteContinuationExtension,
]
